'use client'

import type { ReactNode } from 'react'
import { useLayoutEffect } from 'react'

import { DOM_AUTOMATION_ATTRS } from '@/utilities/domExtensionSanitizerScript'

function stripAutomationAttrsFromDocument(): void {
  try {
    for (const attr of DOM_AUTOMATION_ATTRS) {
      document.querySelectorAll(`[${attr}]`).forEach((node) => node.removeAttribute(attr))
    }
  } catch {
    // ignore
  }
}

function installAutomationAttrSanitizer(): () => void {
  stripAutomationAttrsFromDocument()
  const root = document.documentElement
  if (!root || typeof MutationObserver === 'undefined') {
    return () => {}
  }

  const attrFilter = [...DOM_AUTOMATION_ATTRS]

  const mo = new MutationObserver((records) => {
    for (const r of records) {
      if (r.type === 'attributes' && r.target instanceof Element) {
        const t = r.target
        for (const attr of attrFilter) {
          if (t.hasAttribute(attr)) t.removeAttribute(attr)
        }
      } else if (r.type === 'childList') {
        r.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return
          const el = node as Element
          for (const attr of attrFilter) {
            if (el.hasAttribute(attr)) el.removeAttribute(attr)
            el.querySelectorAll(`[${attr}]`).forEach((x) => x.removeAttribute(attr))
          }
        })
      }
    }
  })
  mo.observe(root, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: attrFilter,
  })

  const onLoad = () => {
    window.setTimeout(() => {
      try {
        mo.disconnect()
      } catch {
        // ignore
      }
    }, 8000)
  }
  window.addEventListener('load', onLoad, { once: true })

  return () => {
    window.removeEventListener('load', onLoad)
    try {
      mo.disconnect()
    } catch {
      // ignore
    }
  }
}

export default function AdminDomExtensionSanitizerProvider({
  children,
}: {
  children: ReactNode
}) {
  useLayoutEffect(() => {
    return installAutomationAttrSanitizer()
  }, [])

  return <>{children}</>
}
